import Images from "../utils/Images";
import { FAQ_ITEMS } from "./faqs";
import { MARKET_PORTAL } from "./simmarkets";

export const COLLECT_NAV = [
  { id: "intro", label: "Introduction" },
  { id: "why-simcollect", label: "Why it matters" },
  { id: "vision", label: "Vision" },
  { id: "functions", label: "Operations" },
  { id: "journey", label: "How it works" },
  { id: "industries", label: "Industries" },
  { id: "videos", label: "Videos" },
  { id: "faqs", label: "FAQs" },
  { id: "demo", label: "Get started" },
];

export const CHALLENGE_GROUPS = [
  {
    n: "01",
    title: "The book",
    copy: "The portfolio is under pressure.",
    items: ["Rising loan defaults", "Growing non-performing loan portfolios"],
  },
  {
    n: "02",
    title: "The desk",
    copy: "The operating model cannot keep up.",
    items: [
      "High operational costs",
      "Manual collection processes",
      "Fragmented recovery systems",
      "Limited operational visibility",
      "Lengthy reconciliation processes",
    ],
  },
  {
    n: "03",
    title: "The relationship",
    copy: "Trust, fairness and the regulator are at risk.",
    items: ["Poor customer engagement", "Compliance challenges", "Increasing legal expenses"],
  },
];

export const VISION_PILLARS = [
  { n: "01", title: "Automation", copy: "Replace spreadsheets and disconnected calls with one recovery lifecycle." },
  { n: "02", title: "Engagement", copy: "Reach customers on the channels they already use — SMS, WhatsApp, USSD and more." },
  { n: "03", title: "Analytics", copy: "Give executives live recovery rates, ageing and collector productivity." },
  { n: "04", title: "Responsibility", copy: "Collect with fairness, transparency, audit trails and compliance built in." },
];

export const OBJECTIVE_GROUPS = [
  {
    n: "01",
    title: "Recover the book",
    copy: "Lift portfolio performance without adding headcount.",
    icon: "trend",
    tone: "navy",
    items: [
      "Improve debt recovery rates.",
      "Reduce non-performing loans.",
      "Lower operational costs.",
    ],
  },
  {
    n: "02",
    title: "Run a smarter desk",
    copy: "Take repetitive recovery work off the collector.",
    icon: "flow",
    tone: "white",
    items: [
      "Automate repetitive recovery activities.",
      "Improve productivity of collection teams.",
      "Support scalable enterprise operations.",
    ],
  },
  {
    n: "03",
    title: "Engage with care",
    copy: "Make repayment easy and communication clear.",
    icon: "message",
    tone: "paper",
    items: [
      "Improve customer communication.",
      "Provide convenient repayment channels.",
      "Support flexible repayment options.",
    ],
  },
  {
    n: "04",
    title: "See and stay accountable",
    copy: "Visibility, ethics and the audit trail in one place.",
    icon: "shield",
    tone: "white",
    items: [
      "Deliver real-time management visibility.",
      "Simplify reconciliation.",
      "Maintain complete audit trails.",
      "Strengthen compliance.",
      "Promote ethical debt collection.",
    ],
  },
];

export const FUNCTIONS = [
  {
    id: "portfolio",
    index: "01",
    short: "Portfolios",
    title: "Debt Portfolio Management",
    kicker: "Onboard every book without chaos",
    copy: "Securely upload and manage debtor portfolios from multiple institutions.",
    items: [
      "Portfolio Upload",
      "Data Validation",
      "Portfolio Processing",
      "Batch Imports",
      "Portfolio History",
      "Duplicate Detection",
      "Secure Multi-Tenant Management",
    ],
    icon: "folder",
  },
  {
    id: "customer",
    index: "02",
    short: "Customers",
    title: "Customer Management",
    kicker: "One profile across the lifecycle",
    copy: "Maintain a complete customer profile throughout the recovery lifecycle.",
    items: [
      "Customer Information",
      "Loan Details",
      "Repayment History",
      "Outstanding Balances",
      "Communication History",
      "Recovery Status",
      "Recovery Stage Tracking",
    ],
    icon: "user",
  },
  {
    id: "cases",
    index: "03",
    short: "Cases",
    title: "Case Management",
    kicker: "From upload to a working file",
    copy: "Automatically generate and manage recovery cases from uploaded debt portfolios.",
    items: [
      "Automated Case Creation",
      "Case Assignment",
      "Case Tracking",
      "Recovery Notes",
      "Escalation Management",
      "Activity History",
      "Document Management",
    ],
    icon: "file",
  },
  {
    id: "workflow",
    index: "04",
    short: "Workflow",
    title: "Intelligent Collection Workflow Engine",
    kicker: "Rules that run the recovery desk",
    copy: "Automate recovery activities based on configurable business rules.",
    items: [
      "Recovery Strategies",
      "Ageing Rules",
      "Reminder Scheduling",
      "Escalation Processes",
      "Payment Arrangements",
      "Legal Review Triggers",
      "SLA Monitoring",
    ],
    icon: "flow",
  },
  {
    id: "workspace",
    index: "05",
    short: "Workspace",
    title: "Contact Centre Workspace",
    kicker: "Every officer, one screen",
    copy: "Provide collection officers with a unified workspace to manage customer interactions.",
    items: [
      "Customer Profiles",
      "Outbound Calls",
      "Inbound Call Management",
      "Repayment Negotiation",
      "Callback Scheduling",
      "Customer Education",
      "Case Updates",
      "Performance Monitoring",
    ],
    icon: "headset",
  },
  {
    id: "comms",
    index: "06",
    short: "Engagement",
    title: "Customer Communication Management",
    kicker: "Meet customers on their channel",
    copy: "Engage customers through multiple digital channels.",
    items: [
      "SMS",
      "Email",
      "WhatsApp",
      "Payment Links",
      "USSD Notifications",
      "Reminder Campaigns",
      "Legal Notices",
      "Personalized Messaging",
    ],
    icon: "message",
  },
  {
    id: "payments",
    index: "07",
    short: "Payments",
    title: "Payment Management",
    kicker: "Make repayment the easy next step",
    copy: "Make repayments simple and convenient.",
    items: [
      "Mobile Money",
      "USSD Payments",
      "Payment Links",
      "Bank Transfers",
      "Partial Payments",
      "Full Payments",
      "Scheduled Payments",
      "Instalment Arrangements",
    ],
    icon: "wallet",
  },
  {
    id: "analytics",
    index: "08",
    short: "Analytics",
    title: "Executive Dashboards & Analytics",
    kicker: "See the book as it moves",
    copy: "Monitor every aspect of recovery performance.",
    items: [
      "Recovery Rates",
      "Portfolio Ageing",
      "Collector Productivity",
      "Payment Trends",
      "Outstanding Balances",
      "Financial Reports",
      "Forecasting",
      "Compliance Reports",
    ],
    icon: "chart",
  },
];

export const BENEFIT_GROUPS = [
  {
    id: "institutions",
    index: "01",
    title: "For financial institutions",
    layout: "wide",
    art: Images.collectBenefitInstitutions,
    spec: "Square 1:1 · 1400×1400 · Soft illustration of a West African bank operations floor / portfolio dashboard. Navy, gold #E5B80B, red #EF4444 accents. Transparent or white background, no logos or readable UI. File: /images/simcollect/benefit-institutions.png",
    items: [
      "Increase recovery rates.",
      "Reduce operational costs.",
      "Reduce non-performing loans.",
      "Improve portfolio visibility.",
      "Automate collections.",
      "Simplify reconciliation.",
      "Improve regulatory reporting.",
      "Strengthen governance.",
      "Improve customer engagement.",
    ],
  },
  {
    id: "teams",
    index: "02",
    title: "For collection teams",
    layout: "wide",
    art: Images.collectBenefitTeams,
    spec: "Square 1:1 · 1400×1400 · Illustration of a collections officer at a unified workspace with headset. Warm, calm, professional. Gold and red accents, no logos. File: /images/simcollect/benefit-teams.png",
    items: [
      "Centralized workspace.",
      "Automated task allocation.",
      "Faster customer access.",
      "Reduced paperwork.",
      "Better workload management.",
      "Improved productivity.",
      "Performance dashboards.",
    ],
  },
  {
    id: "customers",
    index: "03",
    title: "For customers",
    art: Images.collectBenefitCustomers,
    spec: "Square 1:1 · 1400×1400 · Illustration of a West African customer completing a Mobile Money or USSD repayment on a phone. Friendly and dignified. File: /images/simcollect/benefit-customers.png",
    items: [
      "Convenient repayment channels.",
      "Flexible payment arrangements.",
      "Faster issue resolution.",
      "Transparent repayment information.",
      "Improved communication.",
      "Better customer experience.",
    ],
  },
  {
    id: "executives",
    index: "04",
    title: "For executive management",
    art: Images.collectBenefitExecutives,
    spec: "Square 1:1 · 1400×1400 · Illustration of an executive reviewing a recovery dashboard. Clean, strategic, navy and gold. File: /images/simcollect/benefit-executives.png",
    items: [
      "Real-time dashboards.",
      "Better decision-making.",
      "Portfolio performance insights.",
      "Recovery forecasting.",
      "Operational transparency.",
      "Improved governance.",
    ],
  },
  {
    id: "regulators",
    index: "05",
    title: "For regulators",
    art: Images.collectBenefitRegulators,
    spec: "Square 1:1 · 1400×1400 · Illustration of accountability and reporting — a seal, ledger, or balanced scale with a West African professional. Gold and navy, no logos. File: /images/simcollect/benefit-regulators.png",
    items: [
      "Improved accountability.",
      "Better reporting.",
      "Greater transparency.",
      "Enhanced financial stability.",
    ],
  },
];

export const JOURNEY = [
  {
    n: "01",
    title: "Debt Portfolio Submission",
    copy: "Financial institutions securely upload debtor portfolios.",
  },
  {
    n: "02",
    title: "Data Validation",
    copy: "The system validates customer records and identifies missing or duplicate information.",
  },
  {
    n: "03",
    title: "Portfolio Processing",
    copy: "Validated records are automatically converted into recovery cases.",
  },
  {
    n: "04",
    title: "Case Assignment",
    copy: "Recovery cases are intelligently assigned based on workload, customer location, ageing and business rules.",
  },
  {
    n: "05",
    title: "Customer Segmentation",
    copy: "Customers are categorized by days overdue, outstanding balance, risk level, repayment behaviour and collection priority.",
    items: ["Days Overdue", "Outstanding Balance", "Risk Level", "Repayment Behaviour", "Collection Priority"],
  },
  {
    n: "06",
    title: "Automated Customer Engagement",
    copy: "The platform initiates communications through SMS, WhatsApp, email, USSD and payment links.",
    items: ["SMS", "WhatsApp", "Email", "USSD", "Payment Links"],
  },
  {
    n: "07",
    title: "Contact Centre Intervention",
    copy: "If automated engagement is unsuccessful, collection agents interact directly with customers.",
  },
  {
    n: "08",
    title: "Customer Repayment",
    copy: "Customers repay through Mobile Money, USSD, payment links or bank transfers.",
    items: ["Mobile Money", "USSD", "Payment Links", "Bank Transfers"],
  },
  {
    n: "09",
    title: "Payment Processing & Reconciliation",
    copy: "Payments are automatically reconciled with client institutions.",
  },
  {
    n: "10",
    title: "Escalation",
    copy: "Where necessary, unresolved cases progress through approved legal or recovery processes.",
  },
  {
    n: "11",
    title: "Reporting & Analytics",
    copy: "Real-time dashboards update automatically.",
  },
  {
    n: "12",
    title: "Case Closure",
    copy: "Recovered accounts are closed while maintaining complete audit histories.",
  },
];

export const INDUSTRIES = [
  { name: "Commercial Banks", icon: "landmark" },
  { name: "Rural Banks", icon: "building" },
  { name: "Savings & Loans Companies", icon: "wallet" },
  { name: "Microfinance Institutions", icon: "coins" },
  { name: "Credit Unions", icon: "users" },
  { name: "Insurance Companies", icon: "shield" },
  { name: "Digital Lenders", icon: "smartphone" },
  { name: "FinTech Companies", icon: "cpu" },
  { name: "Hire Purchase Companies", icon: "handshake" },
  { name: "Asset Finance Companies", icon: "car" },
  { name: "Educational Loan Providers", icon: "book" },
  { name: "Utility Companies", icon: "zap" },
  { name: "Subscription-Based Businesses", icon: "repeat" },
  { name: "Organizations Managing Receivables", icon: "briefcase" },
];

export const INTEGRATIONS = [
  "Core Banking Systems",
  "Insurance Core Systems",
  "Mobile Money Platforms",
  "Payment Gateways",
  "USSD Platforms",
  "SMS Gateways",
  "WhatsApp Business API",
  "Email Services",
  "CRM Platforms",
  "Credit Reference Bureaus",
  "National Identification Authority (where legally approved)",
];

export const ENTERPRISE = [
  "Multi-Tenant Architecture",
  "Secure Role-Based Access",
  "Audit Trails",
  "API Integrations",
  "Cloud Deployment",
  "Real-Time Monitoring",
  "Executive Dashboards",
];

export const DIFFERENTIATORS = [
  {
    n: "01",
    title: "Intelligent Workflow Automation",
    copy: "Cases move on rules — not spreadsheets — from intake through closure.",
  },
  {
    n: "02",
    title: "Multi-Channel Customer Engagement",
    copy: "Reach customers on SMS, WhatsApp, email, USSD and payment links.",
  },
  {
    n: "03",
    title: "Contact Centre Operations",
    copy: "Agents step in when automation is not enough, with the full file in view.",
  },
  {
    n: "04",
    title: "Flexible Digital Payments",
    copy: "Customers repay through Mobile Money, USSD, payment links or bank transfers.",
  },
  {
    n: "05",
    title: "Executive Dashboards",
    copy: "Watch recovery, ageing and collector productivity as the book moves.",
  },
  {
    n: "06",
    title: "AI-Ready Analytics",
    copy: "Score risk, priority and behaviour as the portfolio grows.",
  },
  {
    n: "07",
    title: "Compliance Monitoring",
    copy: "Fair treatment and a complete audit trail on every case.",
  },
  {
    n: "08",
    title: "Payment Reconciliation",
    copy: "Receipts post back to the client institution without a manual chase.",
  },
  {
    n: "09",
    title: "Performance Reporting",
    copy: "See what recovered, what aged and what still sits on the desk.",
  },
];

export const VIDEOS = [
  { id: "intro", title: "Introduction to SimCollect", kind: "Overview" },
  { id: "upload", title: "Debt Portfolio Upload", kind: "Operations" },
  { id: "cases", title: "Case Management Walkthrough", kind: "Operations" },
  { id: "workspace", title: "Contact Centre Workspace Demo", kind: "Workspace" },
  { id: "engagement", title: "Customer Engagement Automation", kind: "Engagement" },
  { id: "payments", title: "Payment & Reconciliation", kind: "Payments" },
  { id: "dashboard", title: "Executive Dashboard Overview", kind: "Analytics" },
  { id: "reporting", title: "Reporting & Analytics", kind: "Analytics" },
  { id: "ussd", title: "Mobile Money & USSD Payments", kind: "Payments" },
  { id: "practice", title: "Best Practices for Responsible Collections", kind: "Practice" },
];

export const COLLECT_FAQS = FAQ_ITEMS.filter((item) => item.categoryId === "simcollect");

export const INTEREST_AREAS = [
  "Debt Recovery Strategy",
  "Collection Workflow Design",
  "Enterprise Integration",
  "Contact Centre Optimization",
  "Digital Payment Integration",
  "Executive Reporting",
  "Compliance & Governance",
];

export const ORG_TYPES = [
  "Commercial Bank",
  "Rural Bank",
  "Savings & Loans",
  "Microfinance",
  "Credit Union",
  "Insurance",
  "Digital Lender",
  "FinTech",
  "Hire Purchase",
  "Utility",
  "Other",
];

export const APPOINTMENT_TYPES = [
  "Product Demonstration",
  "Recovery Strategy Session",
  "Workflow Design Workshop",
  "Enterprise Integration Review",
  "Executive Presentation",
];

export const RELATED = [
  {
    id: "simmarket",
    name: "SimMarket",
    index: "01",
    copy: "Generate qualified, educated and intent-driven customer leads through Africa's intelligent financial marketplace.",
    href: MARKET_PORTAL,
    cta: "Explore SimMarket",
    logo: Images.simmarket_colored,
  },
  {
    id: "simverify",
    name: "SimVerify",
    index: "02",
    copy: "Verify borrowers, vehicles, devices and behavioural intelligence before approving financial transactions.",
    cta: "Explore SimVerify",
    logo: Images.simverify_logo,
  },
];

export const HERO_MEDIA = {
  src: Images.collectHero,
  alt: "A West African collections operations floor with officers working a digital recovery workspace",
  spec: "Landscape 21:9 · 2400×1080 · Editorial photograph, right-weighted. Subject (operations floor or collections professional) occupies the right 40% of frame; left side quieter and softly lit so headline type stays readable. Warm daylight. Navy, gold #E5B80B, red #EF4444 as environmental accents only. No logos or readable screens. File: /images/simcollect/hero.png",
};

export const INTRO_MEDIA = {
  src: Images.collectIntro,
  alt: "A collections officer reviewing a recovery case with a customer on a digital workspace",
  spec: "Landscape 16:10 · 1920×1200 · West African collections professional at a calm, well-lit desk with a case dashboard. Palette: navy, gold #E5B80B, red #EF4444 as a small accent only. No logos or readable UI text. File: /images/simcollect/intro.png",
};

export const VIDEO_MEDIA = {
  src: Images.collectVideo,
  alt: "SimCollect product demonstration still",
  spec: "Landscape 16:9 · 1920×1080 · Product demo still of a recovery dashboard and case workspace. Gold and red accents, no small UI text. File: /images/simcollect/video.png",
};
